import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { SceneFilter } from "@/types/scene-filter";
import {
  assert,
  convertToDateString,
  GUID,
  validatePrimitive,
} from "@faro-lotv/foundation";
import {
  IElement,
  IElementBase,
  IElementImageCommand,
  IElementImg2d,
  IElementImg360,
  IElementSection,
  IElementTimeSeriesRoom,
  isIElementGenericDataset,
  isIElementGenericVideoRecording,
  isIElementImageCommand,
  isIElementImg2d,
  isIElementImg360,
  isIElementRoomSection,
  isIElementSectionDataSetCapture,
  isIElementTimeSeriesRoom,
  isIElementTimeTravelSection,
} from "@faro-lotv/ielement-types";
import {
  IElementsState,
  isIElementVideoModeCopy,
  newestToOldest,
  selectAncestor,
  selectChildDepthFirst,
  selectChildrenDepthFirst,
  selectIElementChildren,
  selectIElementWorldPosition,
} from "@faro-lotv/project-source";
import { createSelector } from "@reduxjs/toolkit";
import { groupBy } from "es-toolkit";
import { Vector2, Vector3Tuple } from "three";
import { selectImgsFromRoom } from "../mode-selectors";
import { DataSessions3D } from "../select-current-area-data";

const MAX_HORIZONTAL_DISTANCE = 1.5;
const MAX_HORIZONTAL_DISTANCE_SQUARED =
  MAX_HORIZONTAL_DISTANCE * MAX_HORIZONTAL_DISTANCE;
const MAX_VERTICAL_DISTANCE = 2;

/**
 * A memoized selector to compute the proper elements as sorted list to show in the in the dropdown in walk mode
 *
 * @param state The state of the app store
 * @param roomSections The list of all available rooms in the current area
 * @param dataSessions2d The list of all available data session in the current area that contains only pano elements
 * @param dataSessions3d The list of all available data session in the current area that contains only 3d elements
 * @param dataSessions5d The list of all available data session in the current area that contains both 3d and pano elements
 * @param element The current active element in walk mode
 * @param sceneFilter The current filter selected by the user for walk mode
 * @returns The memoized selector
 */
export const selectWalkOverlayDropdownOptions = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, roomSections: IElementSection[]) => roomSections,
      (
        state: RootState,
        roomSections: IElementSection[],
        dataSessions2d: IElementSection[],
      ) => dataSessions2d,
      (
        state: RootState,
        roomSections: IElementSection[],
        dataSessions2d: IElementSection[],
        dataSessions3d: DataSessions3D,
      ) => dataSessions3d,
      (
        state: RootState,
        roomSections: IElementSection[],
        dataSessions2d: IElementSection[],
        dataSessions3d: DataSessions3D,
        dataSessions5d: IElementSection[],
      ) => dataSessions5d,
      (
        state: RootState,
        roomSections: IElementSection[],
        dataSessions2d: IElementSection[],
        dataSessions3d: DataSessions3D,
        dataSessions5d: IElementSection[],
        element: IElement,
      ) => element,
      (
        state: RootState,
        roomSections: IElementSection[],
        dataSessions2d: IElementSection[],
        dataSessions3d: DataSessions3D,
        dataSessions5d: IElementSection[],
        element: IElement,
        sceneFilter: SceneFilter,
      ) => sceneFilter,
    ],
    (
      iElements: IElementsState,
      roomSections: IElementSection[],
      dataSessions2d: IElementSection[],
      dataSessions3d: DataSessions3D,
      dataSessions5d: IElementSection[],
      element: IElement,
      sceneFilter: SceneFilter,
    ) => {
      const state = { iElements };

      const options: TimestampElement[] = [];
      if (
        sceneFilter === SceneFilter.PointCloud ||
        sceneFilter === SceneFilter.Overlay
      ) {
        options.push(...dataSessions3d.pointClouds, ...dataSessions5d);
      }

      if (sceneFilter === SceneFilter.Pano) {
        const ancestor = selectAncestor(
          element,
          (e) =>
            isIElementTimeSeriesRoom(e) ||
            isIElementGenericDataset(e) ||
            isIElementGenericVideoRecording(e),
        )(state);

        // For panorama images in a time series, the reference position is the
        // room's one, not the position of the 360 itself
        const worldPosition = selectIElementWorldPosition(
          ancestor && isIElementTimeSeriesRoom(ancestor)
            ? ancestor.id
            : element.id,
        )(state);
        if (!ancestor) {
          assert(
            isIElementImg2d(element),
            "No valid input for the data session dropdown",
          );
          // Collect the datasets that have at least one pano in the range
          for (const datasession of [...dataSessions2d, ...dataSessions5d]) {
            if (
              isDatasessionInRange(state, datasession, worldPosition, undefined)
            ) {
              options.push(datasession);
            }
          }

          const rootSection = selectAncestor(
            element,
            isIElementRoomSection,
          )(state);

          const timeSeries = selectChildDepthFirst(
            rootSection,
            isIElementTimeSeriesRoom,
          )(state);

          const timepoints = collectRoomTimePoints(state, timeSeries);
          options.push(...timepoints);
        } else if (isIElementTimeSeriesRoom(ancestor)) {
          // Collect the datasets that have at least one pano in the range
          for (const datasession of [...dataSessions2d, ...dataSessions5d]) {
            if (
              isDatasessionInRange(state, datasession, worldPosition, undefined)
            ) {
              options.push(datasession);
            }
          }

          const timepoints = collectRoomTimePoints(state, ancestor);
          options.push(...timepoints);
        } else if (isIElementGenericVideoRecording(ancestor)) {
          // Collect the closest pano for each datasession2d and datasession5d
          for (const datasession of [...dataSessions2d, ...dataSessions5d]) {
            if (
              isDatasessionInRange(state, datasession, worldPosition, undefined)
            ) {
              options.push(datasession);
            }
          }

          // Collect the closest room
          const timepoints = computeClosestRoomTimePoints(
            state,
            roomSections,
            worldPosition,
          );
          options.push(...timepoints);
        } else {
          // Collect the closest pano for each datasession2d and datasession5d
          for (const datasession of dataSessions5d) {
            if (
              isDatasessionInRange(
                state,
                datasession,
                worldPosition,
                MAX_VERTICAL_DISTANCE,
              )
            ) {
              options.push(datasession);
            }
          }
          for (const datasession of dataSessions2d) {
            if (
              // If a dataset does not have a combined point cloud,
              // it will end up in the dataSessions2d array, but we still
              // want to show it in the dropdown
              isIElementSectionDataSetCapture(datasession) &&
              isDatasessionInRange(state, datasession, worldPosition, undefined)
            ) {
              options.push(datasession);
            }
          }
          // Collect the closest room
          const timepoints = computeClosestRoomTimePoints(
            state,
            roomSections,
            worldPosition,
          );
          options.push(...timepoints);
        }
      }

      return options.sort(newestToOldest);
    },
  ),
);

const TEMP_VEC2 = new Vector2();

/**
 * @returns The closest room section to the reference input position
 * @param state The store state containing the iElements
 * @param roomSections The list of all available room sections in the current area
 * @param position The reference 3D world position used a reference for the proximity computation
 */
function computeClosestRoom(
  state: OnlyIElementsStore,
  roomSections: IElementSection[],
  position: Vector3Tuple,
): IElementSection | undefined {
  // Collect the closest room
  let minDistanceSq = Number.POSITIVE_INFINITY;
  let closestRoom: IElementSection | undefined;
  for (const roomSection of roomSections) {
    const panoPosition = selectIElementWorldPosition(roomSection.id)(state);

    const distanceSq = TEMP_VEC2.set(
      position[0] - panoPosition[0],
      position[2] - panoPosition[2],
    ).lengthSq();
    if (
      distanceSq < MAX_HORIZONTAL_DISTANCE_SQUARED &&
      distanceSq < minDistanceSq
    ) {
      minDistanceSq = distanceSq;
      closestRoom = roomSection;
    }
  }
  return closestRoom;
}

/**
 * @returns The list of all section rooms and 2D images that belong to the closest room to the reference position
 * @param state The store state containing the iElements
 * @param roomSections The list of all available room sections in the current area
 * @param showVideoModeCopy Whether the video mode improvements feature is enabled
 * @param position The reference 3D world position used a reference for the proximity computation
 */
function computeClosestRoomTimePoints(
  state: { iElements: IElementsState },
  roomSections: IElementSection[],
  position: Vector3Tuple,
  showVideoModeCopy: boolean = false,
): TimestampElement[] {
  const closestRoom = computeClosestRoom(state, roomSections, position);
  if (closestRoom) {
    const timeseries = selectAncestor(
      closestRoom,
      isIElementTimeSeriesRoom,
    )(state);
    return collectRoomTimePoints(state, timeseries, showVideoModeCopy);
  }
  return [];
}

/**
 * @returns The list of all section rooms and 2D images that belong to the input time series
 * @param state The store state containing the iElements
 * @param showVideoModeCopy Whether the video mode improvements feature is enabled
 * @param timeSeries The time series to check
 */
function collectRoomTimePoints(
  state: { iElements: IElementsState },
  timeSeries: IElementTimeSeriesRoom | undefined,
  showVideoModeCopy: boolean = false,
): TimestampElement[] {
  if (!timeSeries) return [];

  const timepoints: TimestampElement[] = [];
  const rooms = selectIElementChildren(timeSeries.id)(state).filter(
    (section) =>
      (isIElementTimeTravelSection(section) ||
        // ghost room section does not contain 360 pano
        !!selectChildDepthFirst(section, isIElementImg360)(state)) &&
      (showVideoModeCopy ||
        !selectChildDepthFirst(section, isIElementVideoModeCopy)(state)),
  );
  timepoints.push(...rooms);

  const rootSection = selectAncestor(timeSeries, isIElementRoomSection)(state);
  const imgs = rootSection ? selectImgsFromRoom(rootSection)(state) : [];

  // Group 2D photos by date.
  // For each group, create one PhotosGroup as a entry in data sessions dropdown list
  const imgGroups = Object.entries(
    groupBy(imgs, (img) => convertToDateString(img.createdAt)),
  ).map(([, imgs]) => {
    imgs.sort(newestToOldest);
    return {
      id: imgs[0].id,
      createdAt: imgs[0].createdAt,
      photos: imgs,
    };
  });
  timepoints.push(...imgGroups);

  return timepoints;
}

/** Group of 2D photos */
export type PhotosGroup = Pick<IElementBase, "id" | "createdAt"> & {
  photos: IElementImg2d[];
};

/** Type of entries in time stamp dropdown list */
export type TimestampElement = IElement | PhotosGroup;

/**
 * @returns true if the given TimestampElement or GalleryElement is a 2D photos group.
 * @param element element to check
 */
export function isElementPhotosGroup(
  element: TimestampElement | GalleryElement,
): element is PhotosGroup {
  return (
    validatePrimitive(element, "id", "string") &&
    validatePrimitive(element, "createdAt", "string") &&
    "photos" in element &&
    Array.isArray(element.photos) &&
    element.photos.every(isIElementImg2d)
  );
}

/** 360 pano with 2D photo objects */
export type PanoWithPhotoObjects = {
  pano: IElementImg360;
  imageCommands: IElementImageCommand[];
};

/** Type of entries for photo gallery */
export type GalleryElement =
  | IElementImg360
  | PhotosGroup
  | PanoWithPhotoObjects;

/**
 * @returns true if the given photo gallery entry is PanoWithPhotoObjects.
 * @param element element to check
 */
export function isPanoWithPhotoObjects(
  element: GalleryElement,
): element is PanoWithPhotoObjects {
  return (
    "pano" in element &&
    isIElementImg360(element.pano) &&
    "imageCommands" in element &&
    Array.isArray(element.imageCommands) &&
    element.imageCommands.every(isIElementImageCommand)
  );
}

/**
 * @returns true if the input datasession has one pano whose position is closer than MAX_HORIZONTAL_DISTANCE to the
 * reference position
 * @param state The store state containing the iElements
 * @param datasession The datasession to check
 * @param position The reference 3D world position used a reference for the proximity computation
 * @param maxVerticalDistance If defined, it's the maximum vertical distance used for the proximity computation
 */
function isDatasessionInRange(
  state: OnlyIElementsStore,
  datasession: IElementSection,
  position: Vector3Tuple,
  maxVerticalDistance: number | undefined,
): boolean {
  const panos = selectChildrenDepthFirst(datasession, isIElementImg360)(state);
  for (const pano of panos) {
    const panoPosition = selectIElementWorldPosition(pano.id)(state);
    if (
      maxVerticalDistance !== undefined &&
      Math.abs(position[1] - panoPosition[1]) > maxVerticalDistance
    ) {
      continue;
    }

    const horizontalDistanceSq = TEMP_VEC2.set(
      position[0] - panoPosition[0],
      position[2] - panoPosition[2],
    ).lengthSq();
    if (horizontalDistanceSq < MAX_HORIZONTAL_DISTANCE_SQUARED) {
      return true;
    }
  }
  return false;
}

/**
 * A memoized selector to find pano image or PhotosGroup for photo gallery
 *
 * @param state The state of the app store
 * @param worldPosition The world position of current walk element
 * @param elements time drop down element list
 * @returns The memoized selector
 */
export const selectPhotoGalleryImgElements = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: RootState, worldPosition: Vector3Tuple) => worldPosition,
      (
        state: RootState,
        worldPosition: Vector3Tuple,
        elements: TimestampElement[],
      ) => elements,
    ],
    (
      iElements: IElementsState,
      worldPosition: Vector3Tuple,
      elements: TimestampElement[],
    ) => {
      const state = { iElements };
      const imgRecord: Record<GUID, GalleryElement> = {};
      elements.forEach((element) => {
        if (isElementPhotosGroup(element)) {
          imgRecord[element.id] = element;
        } else if (
          isIElementRoomSection(element) ||
          isIElementTimeTravelSection(element)
        ) {
          const pano = selectChildDepthFirst(element, isIElementImg360)(state);
          if (pano) {
            const imageCommands = selectChildrenDepthFirst(
              element,
              isIElementImageCommand,
            )(state);
            if (imageCommands.length) {
              imgRecord[element.id] = { pano, imageCommands };
            } else {
              imgRecord[element.id] = pano;
            }
          }
        } else {
          const panos = selectChildrenDepthFirst(
            element,
            isIElementImg360,
          )(state);
          const pano = findClosestPano(state, panos, worldPosition);
          if (pano) {
            imgRecord[element.id] = pano;
          }
        }
      });
      return imgRecord;
    },
  ),
);

/**
 * A memoized selector to find walk location name
 *
 * @param state The state of the app store
 * @param element The current walk element
 * @returns The memoized selector
 */
export const selectWalkLocation = curryAppSelector(
  createSelector(
    [selectIElementsSlice, (state: RootState, element: IElement) => element],
    (iElements: IElementsState, element: IElement) => {
      const state = { iElements };

      let reference;
      if (isIElementImg2d(element)) {
        reference = selectAncestor(element, isIElementRoomSection)(state);
      } else {
        const parent = selectAncestor(
          element,
          (e) =>
            isIElementTimeSeriesRoom(e) ||
            isIElementGenericDataset(e) ||
            isIElementGenericVideoRecording(e),
        )(state);

        if (parent) {
          reference = isIElementTimeSeriesRoom(parent)
            ? selectAncestor(parent, isIElementRoomSection)(state)
            : parent;
        }
      }
      return reference?.name ?? "";
    },
  ),
);

/**
 * Find the pano that is closest to the given position
 *
 * @param state The store state containing the iElements
 * @param panos The pano list to check
 * @param position The given position in world
 * @returns the closest pano if found
 */
function findClosestPano(
  state: OnlyIElementsStore,
  panos: IElementImg360[],
  position: Vector3Tuple,
): IElementImg360 | undefined {
  // Collect the closest room
  let minDistanceSq = Number.POSITIVE_INFINITY;
  let closestPano: IElementImg360 | undefined;
  for (const pano of panos) {
    const panoPosition = selectIElementWorldPosition(pano.id)(state);

    const distanceSq = TEMP_VEC2.set(
      position[0] - panoPosition[0],
      position[2] - panoPosition[2],
    ).lengthSq();
    if (distanceSq < minDistanceSq) {
      minDistanceSq = distanceSq;
      closestPano = pano;
    }
  }
  return minDistanceSq < MAX_HORIZONTAL_DISTANCE_SQUARED
    ? closestPano
    : undefined;
}
